// src/components/language/LanguageSwitcher.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';

type SupportedLanguage = 'fr' | 'en';

type LanguageSwitcherProps = {
  className?: string;
  compact?: boolean;
};

const normalizeLanguage = (language?: string): SupportedLanguage => {
  if (!language) return 'fr';

  const normalized = language.toLowerCase();

  if (normalized.startsWith('en')) return 'en';

  return 'fr';
};

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  className = '',
  compact = false,
}) => {
  const { i18n, t } = useTranslation();

  const currentLanguage = normalizeLanguage(i18n.language);

  const changeLanguage = async (language: SupportedLanguage) => {
    if (language === currentLanguage) return;

    try {
      await i18n.changeLanguage(language);

      if (typeof window !== 'undefined') {
        window.localStorage.setItem('forfeo_language', language);
        document.documentElement.lang = language;
      }
    } catch (error) {
      console.error('Erreur lors du changement de langue Forfeo:', error);
    }
  };

  const label =
    t('language.switcherLabel', {
      defaultValue: 'Changer la langue',
    }) || 'Changer la langue';

  const buttonBaseClasses =
    'rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-wide transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2';

  const activeClasses =
    'bg-orange-500 text-white shadow-sm';

  const inactiveClasses =
    'bg-white text-slate-600 hover:bg-orange-50 hover:text-orange-700';

  return (
    <div
      className={[
        'inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white/85 p-1 shadow-sm backdrop-blur',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      role="group"
      aria-label={label}
    >
      <button
        type="button"
        onClick={() => changeLanguage('fr')}
        className={[
          buttonBaseClasses,
          currentLanguage === 'fr' ? activeClasses : inactiveClasses,
        ].join(' ')}
        aria-pressed={currentLanguage === 'fr'}
        title="Français"
      >
        {compact ? 'FR' : 'Français'}
      </button>

      <button
        type="button"
        onClick={() => changeLanguage('en')}
        className={[
          buttonBaseClasses,
          currentLanguage === 'en' ? activeClasses : inactiveClasses,
        ].join(' ')}
        aria-pressed={currentLanguage === 'en'}
        title="English"
      >
        {compact ? 'EN' : 'English'}
      </button>
    </div>
  );
};

export default LanguageSwitcher;
